import { compensationApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { BenefitsGateway } from './BenefitsGateway';
import type {
  BenefitCatalogItem, BenefitElection, BenefitOverview, BenefitPlan, BenefitPlanInput, CatalogQuery, CreateBenefitElectionInput, ElectionQuery, ListQuery,
  Paged, UpdateBenefitPlanInput,
} from './types';
import {
  benefitPlanBody, buildQuery, mapBenefitCatalogItem, mapBenefitElection, mapBenefitOverview, mapBenefitPlan, mapList, mapPaged,
} from './wireMapping';

/** Compensation API routes from docs/benefits-deductions-contract.md section 4.1, called through the same-origin `/svc-compensation` prefix. */
export class HttpBenefitsGateway implements BenefitsGateway {
  private readonly request = createJsonClient(compensationApiBaseUrl);

  async listCatalog(query: CatalogQuery = {}, signal?: AbortSignal): Promise<BenefitCatalogItem[]> {
    const qs = buildQuery({ category: query.category, search: query.search, includeHidden: query.includeHidden ? true : undefined });
    return mapList(await this.request<unknown>(`/api/benefit-catalog${qs}`, undefined, signal), mapBenefitCatalogItem);
  }

  async hideCatalogType(code: string): Promise<void> {
    await this.request<unknown>(`/api/benefit-catalog/${encodeURIComponent(code)}/hide`, { method: 'POST' });
  }

  async showCatalogType(code: string): Promise<void> {
    await this.request<unknown>(`/api/benefit-catalog/${encodeURIComponent(code)}/show`, { method: 'POST' });
  }

  async listPlans(query: ListQuery = {}, signal?: AbortSignal): Promise<Paged<BenefitPlan>> {
    const qs = buildQuery({
      search: query.search, category: query.category, status: query.status, effectiveOn: query.effectiveOn, sort: query.sort, page: query.page, pageSize: query.pageSize,
    });
    return mapPaged(await this.request<unknown>(`/api/benefit-plans${qs}`, undefined, signal), mapBenefitPlan);
  }

  async getPlan(id: string, signal?: AbortSignal): Promise<BenefitPlan> {
    return mapBenefitPlan(await this.request<unknown>(`/api/benefit-plans/${encodeURIComponent(id)}`, undefined, signal));
  }

  async createPlan(input: BenefitPlanInput): Promise<BenefitPlan> {
    return mapBenefitPlan(await this.request<unknown>('/api/benefit-plans', { method: 'POST', body: JSON.stringify(benefitPlanBody(input)) }));
  }

  async updatePlan(id: string, input: UpdateBenefitPlanInput): Promise<BenefitPlan> {
    const body = { ...benefitPlanBody(input), expectedVersion: input.expectedVersion };
    return mapBenefitPlan(await this.request<unknown>(`/api/benefit-plans/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }));
  }

  async deactivatePlan(id: string): Promise<BenefitPlan> {
    return mapBenefitPlan(await this.request<unknown>(`/api/benefit-plans/${encodeURIComponent(id)}/deactivate`, { method: 'POST' }));
  }

  async reactivatePlan(id: string): Promise<BenefitPlan> {
    return mapBenefitPlan(await this.request<unknown>(`/api/benefit-plans/${encodeURIComponent(id)}/reactivate`, { method: 'POST' }));
  }

  async getOverview(signal?: AbortSignal): Promise<BenefitOverview> {
    return mapBenefitOverview(await this.request<unknown>('/api/benefit-overview', undefined, signal));
  }

  async listElections(query: ElectionQuery = {}, signal?: AbortSignal): Promise<Paged<BenefitElection>> {
    const qs = buildQuery({ planId: query.planId, employeeId: query.employeeId, status: query.status, page: query.page, pageSize: query.pageSize });
    return mapPaged(await this.request<unknown>(`/api/benefit-elections${qs}`, undefined, signal), mapBenefitElection);
  }

  async listEmployeeElections(employeeId: string, signal?: AbortSignal): Promise<BenefitElection[]> {
    return mapList(await this.request<unknown>(`/api/employees/${encodeURIComponent(employeeId)}/benefit-elections`, undefined, signal), mapBenefitElection);
  }

  async createElection(employeeId: string, input: CreateBenefitElectionInput): Promise<BenefitElection> {
    const body = {
      benefitCodeId: input.benefitCodeId,
      employeeContributionPercentOverride: input.employeeContributionPercentOverride ?? null,
      companyContributionPercentOverride: input.companyContributionPercentOverride ?? null,
      amountOverride: input.amountOverride ?? null,
      effectiveStartDate: input.effectiveStartDate,
      coverageLevel: input.coverageLevel ?? null,
      isWaived: input.isWaived ?? false,
      qualifyingEventReason: input.qualifyingEventReason ?? null,
      enrollmentSource: input.enrollmentSource ?? null,
      effectiveEndDate: input.effectiveEndDate ?? null,
      // Sent only when true: the server's default is "not replacing", which refuses a second open election of the plan.
      ...(input.replaceCurrent ? { replaceCurrent: true } : {}),
    };
    return mapBenefitElection(await this.request<unknown>(`/api/employees/${encodeURIComponent(employeeId)}/benefit-elections`, { method: 'POST', body: JSON.stringify(body) }));
  }

  async endElection(id: string, effectiveEndDate: string, expectedVersion?: number): Promise<BenefitElection> {
    const body = { effectiveEndDate, ...(expectedVersion === undefined ? {} : { expectedVersion }) };
    return mapBenefitElection(await this.request<unknown>(`/api/benefit-elections/${encodeURIComponent(id)}/end`, { method: 'POST', body: JSON.stringify(body) }));
  }
}
